import { useState } from "react";
import {
  Button,
  Input,
  Table,
} from "antd";
import { useNavigate } from "react-router-dom";
import useGlobalConfig from "@/store/global";
import PatientsHeaderButtons from "@/components/PatientsHeaderButtons";
import dayjs from "dayjs";
import { ColumnType, TablePaginationConfig } from "antd/es/table";
import { useQueryPatients, Params } from "@/hooks/usePatients";

const PatientsPage = () => {
  const navigate = useNavigate();
  const { themeColor } = useGlobalConfig();
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [tableParams, setTableParams] = useState<Params>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    page: 1,
    per: 10,
    searchTrigger: 0,
  });

  const { data: result, isLoading } = useQueryPatients({
    ...tableParams,
    searchTrigger,
  });

  const commonTableItems: ColumnType[] = [
    { title: "First Name", dataIndex: "patient_first_name", key: "patient_first_name" },
    { title: "Last Name", dataIndex: "patient_last_name", key: "patient_last_name" },
    { title: "Date of Birth", dataIndex: "date_of_birth", key: "date_of_birth", render: (text: string) => dayjs(text).format('MM-DD-YYYY') },
    { title: "Phone Number", dataIndex: "patient_phone_number", key: "patient_phone_number" },
    { title: "Gender", dataIndex: "gender", key: "gender" },
    { title: "Email", dataIndex: "patient_email_address", key: "patient_email_address" },
    { title: "State", dataIndex: "patient_state", key: "patient_state" },
    {
      title: "Action",
      key: "action",
      align: 'center',
      fixed: 'right',
      render: (_: any, record: any) => (
        <Button
          size="small"
          onClick={() =>
            navigate(`/patients/${record.id}/assistance_requests`)
          }
        >
          View Claims
        </Button>
      ),
    },
  ];

  const handleSearch = () => {
    setSearchTrigger(prev => prev + 1);
    setTableParams(prev => ({
      ...prev,
      page: 1,
    }));
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
  ) => {
    setTableParams(prev => ({
      ...prev,
      page: pagination.current,
      per: pagination.pageSize,
    }));
    setSearchTrigger(prev => prev + 1);
  };

  return (
    <>
      <PatientsHeaderButtons />
      <div className="flex flex-col gap-y-4">
        <h2 className="text-xl font-bold">Search patient</h2>
        <div className="flex flex-col md:flex-row gap-y-2 md:gap-x-2 justify-start">
          <Input
            className="w-full md:w-[200px]"
            placeholder="First name"
            allowClear
            value={tableParams.firstName}
            onChange={(e) => setTableParams({
              ...tableParams,
              firstName: e.target.value
            })}
          />
          <Input
            className="w-full md:w-[200px]"
            placeholder="Last name"
            allowClear
            value={tableParams.lastName}
            onChange={(e) => setTableParams({
              ...tableParams,
              lastName: e.target.value
            })}
          />
          <Input
            className="w-full md:w-[200px]"
            placeholder="DOB (mm-dd-yyyy)"
            allowClear
            value={tableParams.dateOfBirth}
            onChange={(e) => setTableParams({
              ...tableParams,
              dateOfBirth: e.target.value
            })}
          />
          <Button
            loading={isLoading}
            type="primary"
            disabled={isLoading}
            style={{ backgroundColor: themeColor, borderColor: themeColor }}
            onClick={handleSearch}
          >
            Search
          </Button>
        </div>
      </div>
      <Table
        loading={isLoading}
        rowKey="id"
        bordered
        className="mt-4"
        pagination={{
          ...result?.pagination,
          showSizeChanger: false,
          current: tableParams.page,
          pageSize: tableParams.per,
        }}
        scroll={{
          x: 'max-content',
        }}
        onChange={handleTableChange}
        dataSource={result?.data || []}
        columns={commonTableItems}
      />
    </>
  );
};

export default PatientsPage;
